import React, { useEffect, useState } from 'react';
import { Typography } from '@mui/material';

import { OnboardingWrapper } from '../../../components';
import { useOnboarding } from '../../../data-access';
import PlanPreviewBubble from '../../../components/AiAssistantChat/bubbles/PlanPreviewBubble';
import SchedulerStatusMessages from '../../../components/Chat/SchedulerChatUI/SchedulerStatusMessages';
import { useAllPlanSuggestedModal } from '../../../hooks/useAllPlanSuggestedModal';
import { trackEventMixpanel } from '../../../utils';
import '../../../components/AiAssistantChat/AssistantChatContent/AssistantChatContent.scss';
import './AiPlanPreviewPage.scss';

const AI_PLAN_PREVIEW_LOADING_HOLD_MS = 1000;

/**
 * Onboarding plan preview. Plan state is driven here (auto-run on first visit)
 * and rendered through the same PlanPreviewBubble used by Plan My Day in the
 * home AI chat, so suggestions look and behave identically to the chat view.
 */
const AiPlanPreviewPage: React.FC = () => {
  const { currentOnboardingStep, finishOnboardingAfterPaywall } = useOnboarding();
  const { schedules, isLoading, statusLines } = useAllPlanSuggestedModal({
    autoRunOnFirstVisit: true,
  });

  const [loading, setLoading] = useState(true);
  const [canSyncLoadingFromHook, setCanSyncLoadingFromHook] = useState(false);

  useEffect(() => {
    const id = window.setTimeout(
      () => setCanSyncLoadingFromHook(true),
      AI_PLAN_PREVIEW_LOADING_HOLD_MS
    );
    return () => clearTimeout(id);
  }, []);

  useEffect(() => {
    if (!canSyncLoadingFromHook) {
      return;
    }
    setLoading(isLoading || statusLines.length > 0);
  }, [canSyncLoadingFromHook, isLoading, statusLines.length]);

  const showLoading = loading;
  const hasSchedules = schedules.length > 0;

  const handleGoHome = () => {
    finishOnboardingAfterPaywall();
    trackEventMixpanel('onboarding_step_next', {
      step: 'dashboard',
    });
  };

  return (
    <OnboardingWrapper
      currentOnboardingStep={currentOnboardingStep}
      title={showLoading ? 'Planning your day...' : "Here's your day!"}
      hideBackButton
      buttons={[
        {
          text: 'Go to Dashboard',
          disabled: showLoading,
          onPress: handleGoHome,
          variant: 'contained',
        },
      ]}
    >
      <div className="ai-plan-preview-page">
        {!canSyncLoadingFromHook ? (
          <SchedulerStatusMessages statusLines={[]} emptyFallback="Analyzing your schedule…" />
        ) : (
          <PlanPreviewBubble />
        )}

        {!showLoading && hasSchedules && (
          <Typography variant="body2" className="ai-plan-preview-page__caption">
            Your daily plan will adapt as your energy, tasks, and routine change.
          </Typography>
        )}
      </div>
    </OnboardingWrapper>
  );
};

export default AiPlanPreviewPage;
